// S3-04/S3-05 (ARCH-SESSION-CONTEXT.md): bind this tab's requests to the identity it SHOWS.
//
// The browser's session can be replaced from another tab. The server answers each request for whoever
// the cookie names now, so a stale tab would render the new account's data under the old labels, or
// act as the new account. Each covered request therefore carries the (user, company) pair the tab
// shows, captured together; the server refuses a mismatch with 409 `session_changed` before any effect.
// This guard attaches that pair, maps the refusal, and, as a fallback the server cannot provide, checks
// the identity again before an answer is released to the tab and stops a stream when the tab's
// generation is retired. It decides nothing about access.
//
// What it guarantees, when the identity can be read: a covered answer or stream is delivered to the tab
// only while the tab's identity still matches the server's, and an unreadable identity WITHHOLDS the
// answer without declaring a change. A request already admitted under the old valid identity may have
// committed there; this is not a cancellation or rollback.

export const EXPECTED_USER_HEADER = 'x-poneva-expected-user-id';
export const EXPECTED_COMPANY_HEADER = 'x-poneva-expected-company-id';
/** Header encoding of a VERIFIED unassigned company (onboarding operations only); never "unknown". */
export const NO_COMPANY = 'none';

/** The identity this tab shows. `companyId` undefined = not yet fixed (unknown); null = verified none. */
export type ShownIdentity = { userId: string; companyId: string | null | undefined };
export type CurrentIdentity = {
  /** null: signed out. */
  userId: string | null;
  /** null: verified unassigned. */
  companyId: string | null;
  account: string | null;
  company: string | null;
};
export type SessionVerdict = 'same' | 'changed' | 'unknown';

export class SessionChangedError extends Error {
  constructor() {
    super('session_changed');
    this.name = 'SessionChangedError';
  }
}
/** The identity could not be read, so the answer is withheld; the tab is NOT declared changed. */
export class SessionUnverifiedError extends Error {
  constructor() {
    super('session_unverified');
    this.name = 'SessionUnverifiedError';
  }
}

export function compareSession(
  shown: ShownIdentity,
  current: CurrentIdentity | null,
): SessionVerdict {
  if (!current) return 'unknown';
  if (current.userId === null || current.userId !== shown.userId) return 'changed';
  if (shown.companyId !== undefined && current.companyId !== shown.companyId) return 'changed';
  return 'same';
}

export type SessionGuard = ReturnType<typeof createSessionGuard>;

export function createSessionGuard(options: {
  /** ONE coherent read: the existing GET /api/v1/company (user and company from the same answer). */
  fetchIdentity: () => Promise<CurrentIdentity | null>;
  onChange: (current: CurrentIdentity | null) => void;
}) {
  let shown: ShownIdentity | null = null;
  let changed: { current: CurrentIdentity | null } | null = null;
  let retired = false; // terminal: this guard's actions never dispatch or deliver again
  let generation = 0;
  let tick = 0; // a monotonic order for "began after this answer arrived"
  let flight: Promise<SessionVerdict> | null = null;
  let flightAt = 0;
  let lastDone: { at: number; verdict: SessionVerdict } | null = null;

  /** Apply one identity observation. The first answer for the shown user fixes the shown company. */
  const observe = (current: CurrentIdentity | null): SessionVerdict => {
    if (!shown) return 'unknown';
    if (current && current.userId === shown.userId && shown.companyId === undefined)
      shown = { ...shown, companyId: current.companyId };
    const verdict = compareSession(shown, current);
    if (verdict === 'changed' && !changed) {
      changed = { current };
      generation += 1;
      if (!retired) options.onChange(current); // a retired guard never speaks for the tab
    }
    return verdict;
  };

  const verify = (): Promise<SessionVerdict> => {
    if (changed) return Promise.resolve('changed');
    if (!shown) return Promise.resolve('unknown');
    if (flight) return flight;
    const baseline = shown;
    const startedAt = ++tick;
    flightAt = startedAt;
    flight = options
      .fetchIdentity()
      .then((current): SessionVerdict => {
        if (shown !== baseline && shown?.userId !== baseline.userId) return 'unknown'; // the tab adopted another session meanwhile
        const verdict = observe(current);
        lastDone = { at: startedAt, verdict };
        return verdict;
      })
      .finally(() => {
        flight = null;
      });
    return flight;
  };

  return {
    /** Terminal. The tab no longer shows the identity this guard served: every action bound to it ends. */
    retire() {
      retired = true;
      generation += 1;
    },
    /** The tab shows (or stops showing) a signed-in account. A new account retires old actions. */
    setShown(userId: string | null) {
      shown = userId ? { userId, companyId: undefined } : null;
      changed = null;
      generation += 1;
      lastDone = null;
    },
    /** Company operations need the shown company fixed by an identity read first. */
    async ready(): Promise<SessionVerdict> {
      if (changed) return 'changed';
      if (shown && shown.companyId !== undefined) return 'same';
      const verdict = await verify();
      return verdict === 'same' && shown?.companyId === undefined ? 'unknown' : verdict;
    },
    /** The pair and generation to attach to ONE action, captured together. null: not yet known. */
    capture(): { userId: string; companyId: string | null; generation: number } | null {
      if (!shown || shown.companyId === undefined) return null;
      return { userId: shown.userId, companyId: shown.companyId, generation };
    },
    /**
     * The ONE explicit exception to "never rebase": accepting an invitation moves a verified-unassigned
     * person into a company. The old onboarding generation is retired and, when the server's answer names
     * the SAME user's accepted company, that company is adopted; with no coherent answer (lost reply) the
     * company becomes unknown and the next use bootstraps from GET /api/v1/company. Never a mutation retry.
     */
    retireOnboarding(
      /** The generation the accepting action began under: a newer one makes this a no-op. */
      actionGeneration: number,
      accepted?: { userId: string; companyId: string },
    ) {
      if (!shown || changed || retired || actionGeneration !== generation) return;
      generation += 1;
      lastDone = null;
      shown =
        accepted && accepted.userId === shown.userId
          ? { userId: shown.userId, companyId: accepted.companyId }
          : { userId: shown.userId, companyId: undefined };
    },
    /** The bootstrap read (GET /api/v1/company) is itself an identity observation. */
    observe,
    /** The server refused with session_changed: stop the tab; read who is signed in for the notice. */
    async declareChanged(): Promise<void> {
      if (changed) return;
      // Stop FIRST (no further dispatch, no further delivery), then try to name who is signed in.
      changed = { current: null };
      generation += 1;
      if (!retired) options.onChange(null);
      let current: CurrentIdentity | null = null;
      try {
        current = await options.fetchIdentity();
      } catch {
        current = null;
      }
      if (current && !retired) {
        changed = { current };
        options.onChange(current);
      }
    },
    /** The tab was hidden or lost focus, or regained it: re-check now. */
    verify,
    /** An order mark for an answer that has just arrived (see verifyAfter). */
    mark(): number {
      return ++tick;
    },
    /**
     * An identity read that BEGAN after `mark` was taken: reuses one in flight or finished that started
     * later, otherwise starts a new one. Answers that arrive together share one read.
     */
    verifyAfter(mark: number): Promise<SessionVerdict> {
      if (changed) return Promise.resolve('changed');
      if (lastDone && lastDone.at > mark) return Promise.resolve(lastDone.verdict);
      if (flight && flightAt > mark) return flight;
      const earlier = flight ?? Promise.resolve('unknown' as SessionVerdict);
      return earlier.then(() => verify());
    },
    get changed() {
      return changed !== null || retired;
    },
    get generation() {
      return generation;
    },
  };
}

// The browser route groups this shell calls that act for a signed-in person's company (ARCH: coverage is an
// explicit list, not "everything except auth"). Public authentication entry/exit, OAuth and session
// bootstrap keep their own contracts and are absent; authenticated account Settings mutations are listed.
const COVERED_GROUPS = [
  '/api/chat',
  '/api/v1/company',
  '/api/v1/company-invitation',
  '/api/v1/threads',
  '/api/v1/memories',
  '/api/v1/settings',
  '/api/v1/history',
  '/api/v1/billing',
  '/api/v1/usage',
  '/api/v1/actions',
  '/api/v1/speech',
  '/api/v1/starter-prompts',
  '/api/v1/feedback', // and /api/v1/feedback/note
  '/api/v1/auth/account',
  '/api/v1/auth/password/change',
  '/api/v1/auth/password/status',
  '/api/v1/auth/sessions/revoke-all', // Log out everywhere (normal logout is this device only and not listed)
  '/api/storefront', // the company's shopping assistants (Storefront)
] as const;

// Artifact runtime controls: exact paths, not a prefix (`/api/artifacts/:artifactId/runtime/{bootstrap,renew,revoke,operation}`).
const ARTIFACT_RUNTIME = /^\/api\/artifacts\/[^/]+\/runtime\/(bootstrap|renew|revoke|operation)$/u;

export function isCoveredPath(pathname: string): boolean {
  return (
    COVERED_GROUPS.some((group) => pathname === group || pathname.startsWith(`${group}/`)) ||
    ARTIFACT_RUNTIME.test(pathname)
  );
}

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

const BOOTSTRAP_PATH = '/api/v1/company';
// Routes that end every session, this tab's included: their successful answer carries no data, and a signed-out
// identity after it is their intended result, not a replaced one, so it is not checked afterwards.
const SESSION_ENDING_PATHS: ReadonlySet<string> = new Set([
  '/api/v1/auth/password/change',
  '/api/v1/auth/sessions/revoke-all',
]);
const ACCEPT_INVITATION_PATH = '/api/v1/company-invitation/accept';

async function isSessionChangedRefusal(response: Response): Promise<boolean> {
  if (response.status !== 409) return false;
  try {
    return (await response.clone().json())?.error === 'session_changed';
  } catch {
    return false;
  }
}

function identityFromBootstrap(body: unknown): CurrentIdentity | null {
  const b = body as {
    userId?: unknown;
    company?: { id?: unknown; name?: unknown } | null;
    membership?: { userId?: unknown } | null;
  } | null;
  const userId =
    typeof b?.userId === 'string'
      ? b.userId
      : typeof b?.membership?.userId === 'string'
        ? b.membership.userId
        : null;
  if (!userId) return null; // no coherent user in the answer: unknown, never a guess
  return {
    userId,
    companyId: typeof b?.company?.id === 'string' ? b.company.id : null,
    account: null,
    company: typeof b?.company?.name === 'string' ? b.company.name : null,
  };
}

/** Stop emitting a stream to the tab when its generation is retired; never buffers. */
function retireOnChange(
  response: Response,
  guard: Pick<SessionGuard, 'changed' | 'generation'>,
  generation: number,
): Response {
  const body = response.body;
  if (!body) return response;
  const reader = body.getReader();
  const stream = new ReadableStream<Uint8Array>({
    async pull(controller) {
      if (guard.changed || guard.generation !== generation) {
        await reader.cancel().catch(() => {});
        controller.error(new SessionChangedError());
        return;
      }
      const { done, value } = await reader.read();
      // A chunk that arrives after the generation was retired is never emitted.
      if (guard.changed || guard.generation !== generation) {
        await reader.cancel().catch(() => {});
        controller.error(new SessionChangedError());
        return;
      }
      if (done) controller.close();
      else controller.enqueue(value);
    },
    cancel: (reason) => reader.cancel(reason),
  });
  return new Response(stream, {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
}

/** A fetch that carries the shown identity on covered same-origin requests and withholds answers for a replaced one. */
export function guardFetch(
  raw: FetchLike,
  guard: Pick<
    SessionGuard,
    | 'ready'
    | 'capture'
    | 'observe'
    | 'declareChanged'
    | 'retireOnboarding'
    | 'mark'
    | 'verifyAfter'
    | 'changed'
    | 'generation'
  >,
  origin: string,
): FetchLike {
  return async (input, init) => {
    let url: URL;
    try {
      const href =
        typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      url = new URL(href, origin);
    } catch {
      return raw(input, init);
    }
    // Exact origin equality: a protocol-relative or absolute foreign URL never receives the identity.
    if (url.origin !== origin || !isCoveredPath(url.pathname)) return raw(input, init);

    // The action's generation, taken before any await. It is checked again after EVERY await below
    // (bootstrap, dispatch, each body read, the identity check): an action never outlives its generation.
    let expected = guard.generation;
    const method = (
      init?.method ?? (input instanceof Request ? input.method : 'GET')
    ).toUpperCase();
    const bootstrap = method === 'GET' && url.pathname === BOOTSTRAP_PATH;
    const acceptInvitation = method === 'POST' && url.pathname === ACCEPT_INVITATION_PATH;
    let response: Response | null = null;
    const assertCurrent = () => {
      if (guard.changed || guard.generation !== expected) {
        response?.body?.cancel().catch(() => {});
        throw new SessionChangedError();
      }
    };
    assertCurrent();
    if (!bootstrap) {
      const ready = await guard.ready();
      assertCurrent(); // retired while the bootstrap was awaited: nothing is dispatched
      if (ready === 'changed') throw new SessionChangedError();
      if (ready === 'unknown') throw new SessionUnverifiedError();
    }
    // Captured ONCE, together, before any further async work; never refreshed into this action.
    const cap = guard.capture();
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    if (cap) {
      headers.set(EXPECTED_USER_HEADER, cap.userId);
      headers.set(EXPECTED_COMPANY_HEADER, cap.companyId ?? NO_COMPANY);
    }
    try {
      response = await raw(
        input instanceof Request ? new Request(input, { ...init, headers }) : input,
        input instanceof Request ? undefined : { ...init, headers },
      );
    } catch (error) {
      // A lost reply may still have accepted the invitation: bootstrap coherently next, never retry it.
      // Only a verified-none onboarding can become unknown; an assigned pair is never cleared to rebase.
      if (acceptInvitation && cap?.companyId === null) guard.retireOnboarding(expected);
      throw error;
    }

    if (await isSessionChangedRefusal(response)) {
      await guard.declareChanged();
      throw new SessionChangedError();
    }
    assertCurrent();
    if (bootstrap && response.ok) {
      // The one coherent snapshot: the user must be the one this tab shows before any company data is adopted.
      let identity: CurrentIdentity | null = null;
      try {
        identity = identityFromBootstrap(await response.clone().json());
      } catch {
        identity = null;
      }
      assertCurrent(); // the tab changed account while the body was read: it is not this action's answer
      const verdict = guard.observe(identity);
      if (verdict === 'changed') throw new SessionChangedError();
      if (verdict === 'unknown') throw new SessionUnverifiedError();
      return response;
    }
    if (acceptInvitation && response.ok) {
      // ONLY none -> the accepted company of THIS user, or the same company again (a replay).
      let identity: CurrentIdentity | null = null;
      try {
        identity = identityFromBootstrap(await response.clone().json());
      } catch {
        identity = null;
      }
      assertCurrent();
      if (identity && identity.userId !== cap?.userId) {
        guard.observe(identity);
        throw new SessionChangedError();
      }
      if (identity && identity.companyId && cap && cap.companyId === null) {
        guard.retireOnboarding(expected, {
          userId: identity.userId as string,
          companyId: identity.companyId,
        });
        expected = guard.generation; // this action's own, intended rebase
      } else if (identity && identity.companyId && cap && cap.companyId === identity.companyId) {
        // same company replay: nothing to rebase
      } else if (identity && identity.companyId && cap) {
        guard.observe(identity); // another company for an already-assigned person: a changed session
        throw new SessionChangedError();
      } else {
        // No coherent answer: withhold this body. Only a captured verified-none onboarding becomes
        // unknown (bootstrap next); an already-assigned pair is kept exactly as captured.
        if (cap?.companyId === null) guard.retireOnboarding(expected);
        throw new SessionUnverifiedError();
      }
    }
    if (!response.ok && response.status !== 304) return response; // refusals carry no company data
    if (method === 'POST' && SESSION_ENDING_PATHS.has(url.pathname)) return response;
    // Before the answer (or a stream's reader) is released: the identity read after it arrived must match,
    // and this action's generation must still be the tab's. Unknown withholds without declaring a change.
    const arrived = guard.mark();
    const verdict = await guard.verifyAfter(arrived);
    assertCurrent();
    if (verdict === 'changed') throw new SessionChangedError();
    if (verdict === 'unknown') {
      response.body?.cancel().catch(() => {});
      throw new SessionUnverifiedError();
    }
    const contentType = response.headers.get('content-type') ?? '';
    if (cap && contentType.includes('text/event-stream'))
      return retireOnChange(response, guard, expected);
    return response;
  };
}
